/**
 * Input of the calculation layer and a pre-built index over it. Only confirmed records count:
 * drafts and voided records never enter a financial figure.
 */
import type { IncomeType } from "@/domain/income";
import type {
  BalanceAnchor,
  BudgetLine,
  Category,
  CategoryGroup,
  Expense,
  Income,
  IncomeBudget,
  YearMonth,
} from "@/domain/model";
import { Decimal } from "@/domain/money";

export interface FinanceData {
  groups: CategoryGroup[];
  categories: Category[];
  expenses: Expense[];
  incomes: Income[];
  budgets: BudgetLine[];
  incomeBudgets: IncomeBudget[];
  anchors: BalanceAnchor[];
}

export interface FinanceIndex {
  groups: Map<string, CategoryGroup>;
  categories: Map<string, Category>;
  expensesByMonth: Map<YearMonth, Expense[]>;
  incomesByMonth: Map<YearMonth, Income[]>;
  budgetsByMonth: Map<YearMonth, Map<string, Decimal>>;
  incomeBudgets: Map<YearMonth, IncomeBudget>;
  /** Effective anchor per month (the most recently updated one when several share a month). */
  anchors: Map<YearMonth, BalanceAnchor>;
  /** Every month that has confirmed income or expenses, sorted. */
  dataMonths: YearMonth[];
}

function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

export function buildFinanceIndex(data: FinanceData): FinanceIndex {
  const expensesByMonth = new Map<YearMonth, Expense[]>();
  for (const e of data.expenses) if (e.status === "confirmed") push(expensesByMonth, e.accountingMonth, e);

  const incomesByMonth = new Map<YearMonth, Income[]>();
  for (const i of data.incomes) if (i.status === "confirmed") push(incomesByMonth, i.accountingMonth, i);

  const budgetsByMonth = new Map<YearMonth, Map<string, Decimal>>();
  for (const b of data.budgets) {
    let m = budgetsByMonth.get(b.accountingMonth);
    if (!m) budgetsByMonth.set(b.accountingMonth, (m = new Map()));
    m.set(b.categoryId, new Decimal(b.amount));
  }

  const anchors = new Map<YearMonth, BalanceAnchor>();
  for (const a of data.anchors) {
    const current = anchors.get(a.effectiveMonth);
    if (!current || a.updatedAt > current.updatedAt) anchors.set(a.effectiveMonth, a);
  }

  const dataMonths = [...new Set([...expensesByMonth.keys(), ...incomesByMonth.keys()])].sort();

  return {
    groups: new Map(data.groups.map((g) => [g.id, g])),
    categories: new Map(data.categories.map((c) => [c.id, c])),
    expensesByMonth,
    incomesByMonth,
    budgetsByMonth,
    incomeBudgets: new Map(data.incomeBudgets.map((b) => [b.accountingMonth, b])),
    anchors,
    dataMonths,
  };
}

export const INCOME_TYPE_ORDER: readonly IncomeType[] = ["salary", "bonus", "reimbursement", "gift", "other"];
